/**
 * File: assets/basari-prescription.js
 * Purpose: Prescription Framework — controller / service / validation / provider.
 * Sprint: 1B.12 Phase A — Manual Foundation Architecture (FROZEN)
 * Extended: 1B.13 Phase A — Upload Prescription architecture & state foundation
 * Extended: 1B.14 Phase A — Temporary Confirm → lens-addons navigation bridge
 * Owner: Basari Theme / PDP
 * Dependencies: none (drawer consumes via window.BasariPrescription)
 *
 * Architecture:
 *   Drawer → Router → PrescriptionController
 *     → PrescriptionService (Manual) → ValidationService + Provider
 *     → UploadService → FileValidationService + Provider
 *     → lensFlow.manualPrescription | lensFlow.uploadPrescription
 *     → proceedToLensAddons() → Router.open("lens-addons")
 *
 * Future strategies (same controller, dedicated services):
 *   Manual | Upload | My Account | Add Later | VisionCarePro | Eye Test | Review
 *
 * Out of scope (1B.13 Phase B+ / 1B.14 business):
 *   - Upload UI / drag-drop / progress / Liquid presentation
 *   - Real file transfer / server communication
 *   - Real VisionCarePro provider (integration method unknown)
 *   - Lens add-ons pricing / recommendations / cart
 */
(function (global) {
  'use strict';

  var BasariPrescription = global.BasariPrescription || {};

  /* ---------------------------------------------------------------------------
   * Events — reusable across Manual / Upload / Provider / Account flows
   * ------------------------------------------------------------------------- */
  var EVENTS = {
    INITIALIZED: 'basari:prescription:initialized',
    UPDATED: 'basari:prescription:updated',
    SAVED: 'basari:prescription:saved',
    VALIDATION_REQUESTED: 'basari:prescription:validation-requested',
    VALIDATION_PASSED: 'basari:prescription:validation-passed',
    VALIDATION_FAILED: 'basari:prescription:validation-failed',
    NAVIGATION_REQUESTED: 'basari:prescription:navigation-requested',
    PROVIDER_READY: 'basari:prescription:provider-ready',
    RESET: 'basari:prescription:reset',
    /* Upload strategy events — UI binds later; architecture emits now */
    UPLOAD_INITIALIZED: 'basari:prescription:upload:initialized',
    UPLOAD_UPDATED: 'basari:prescription:upload:updated',
    UPLOAD_FILE_SELECTED: 'basari:prescription:upload:file-selected',
    UPLOAD_FILE_REMOVED: 'basari:prescription:upload:file-removed',
    UPLOAD_VALIDATION_FAILED: 'basari:prescription:upload:validation-failed',
    UPLOAD_READY: 'basari:prescription:upload:ready',
    UPLOAD_SUBMISSION_PREPARED: 'basari:prescription:upload:submission-prepared',
    UPLOAD_RESET: 'basari:prescription:upload:reset'
  };

  var SOURCES = {
    MANUAL: 'manual',
    UPLOAD: 'upload',
    PROVIDER: 'provider',
    ACCOUNT: 'account',
    SAVED: 'saved',
    LATER: 'later',
    EYE_TEST: 'eye_test',
    REVIEW: 'review'
  };

  var PD_MODES = {
    SINGLE: 'single',
    DUAL: 'dual'
  };

  var VALIDATION_STATUS = {
    IDLE: 'idle',
    PENDING: 'pending',
    VALID: 'valid',
    INVALID: 'invalid'
  };

  /**
   * Upload lifecycle — single status field (no overlapping booleans).
   * idle → selecting → validating → ready | error
   * ready → submitting → completed | error
   */
  var UPLOAD_STATUS = {
    IDLE: 'idle',
    SELECTING: 'selecting',
    VALIDATING: 'validating',
    READY: 'ready',
    SUBMITTING: 'submitting',
    COMPLETED: 'completed',
    ERROR: 'error'
  };

  var UPLOAD_FILE_LIMITS = {
    MAX_FILES: 1,
    MAX_SIZE_BYTES: 5 * 1024 * 1024
  };

  var UPLOAD_ALLOWED_MIME = {
    'application/pdf': true,
    'image/jpeg': true,
    'image/jpg': true,
    'image/png': true,
    'image/heic': true,
    'image/heif': true
  };

  var UPLOAD_ALLOWED_EXTENSIONS = {
    pdf: true,
    jpg: true,
    jpeg: true,
    png: true,
    heic: true,
    heif: true
  };

  var DEFAULT_PROVIDER = 'mock';
  var providerRegistry = {};

  function emitSafe(emitter, name, detail) {
    if (typeof emitter !== 'function') return;
    try {
      emitter(name, detail || {});
    } catch (error) {
      // Host emit failures must not break prescription flow.
    }
  }

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function shallowClone(value) {
    if (Array.isArray(value)) return value.slice();
    if (!isObject(value)) return value;
    var out = {};
    var key;
    for (key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        out[key] = value[key];
      }
    }
    return out;
  }

  function assign() {
    var target = arguments[0];
    var i;
    var source;
    var key;
    if (!isObject(target)) target = {};
    for (i = 1; i < arguments.length; i += 1) {
      source = arguments[i];
      if (!isObject(source)) continue;
      for (key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  }

  function createEye(overrides) {
    var eye = {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    };
    if (!isObject(overrides)) return eye;
    if (overrides.sphere != null) eye.sphere = String(overrides.sphere);
    if (overrides.cylinder != null) eye.cylinder = String(overrides.cylinder);
    if (overrides.axis != null) eye.axis = String(overrides.axis);
    if (overrides.add != null) eye.add = String(overrides.add);
    return eye;
  }

  function createValidationState(overrides) {
    var state = {
      status: VALIDATION_STATUS.IDLE,
      errors: {},
      sections: {}
    };
    if (!isObject(overrides)) return state;
    if (overrides.status) state.status = String(overrides.status);
    if (isObject(overrides.errors)) state.errors = shallowClone(overrides.errors);
    if (isObject(overrides.sections)) state.sections = shallowClone(overrides.sections);
    return state;
  }

  /**
   * ONE normalized prescription model.
   * Manual / Upload / VisionCarePro / Account / Saved must all map into this shape.
   */
  function createNormalizedPrescription(overrides) {
    overrides = isObject(overrides) ? overrides : {};

    return {
      id: overrides.id != null ? overrides.id : null,
      source: overrides.source ? String(overrides.source) : SOURCES.MANUAL,
      patientName: overrides.patientName != null ? String(overrides.patientName) : '',
      rightEye: createEye(overrides.rightEye),
      leftEye: createEye(overrides.leftEye),
      pdMode: overrides.pdMode === PD_MODES.DUAL ? PD_MODES.DUAL : PD_MODES.SINGLE,
      pd: {
        single: overrides.pd && overrides.pd.single != null ? String(overrides.pd.single) : '',
        right: overrides.pd && overrides.pd.right != null ? String(overrides.pd.right) : '',
        left: overrides.pd && overrides.pd.left != null ? String(overrides.pd.left) : ''
      },
      notes: overrides.notes != null ? String(overrides.notes) : '',
      dob: overrides.dob != null ? String(overrides.dob) : '',
      gender: overrides.gender != null ? String(overrides.gender) : '',
      meta: isObject(overrides.meta) ? shallowClone(overrides.meta) : {},
      validation: createValidationState(overrides.validation),
      updatedAt: overrides.updatedAt != null ? overrides.updatedAt : null
    };
  }

  function clonePrescription(prescription) {
    return createNormalizedPrescription(prescription || {});
  }

  function mergePrescription(base, patch) {
    var next = clonePrescription(base);
    if (!isObject(patch)) return next;

    if (patch.id !== undefined) next.id = patch.id;
    if (patch.source != null) next.source = String(patch.source);
    if (patch.patientName != null) next.patientName = String(patch.patientName);
    if (isObject(patch.rightEye)) next.rightEye = createEye(assign({}, next.rightEye, patch.rightEye));
    if (isObject(patch.leftEye)) next.leftEye = createEye(assign({}, next.leftEye, patch.leftEye));
    if (patch.pdMode === PD_MODES.SINGLE || patch.pdMode === PD_MODES.DUAL) next.pdMode = patch.pdMode;
    if (isObject(patch.pd)) {
      next.pd = {
        single: patch.pd.single != null ? String(patch.pd.single) : next.pd.single,
        right: patch.pd.right != null ? String(patch.pd.right) : next.pd.right,
        left: patch.pd.left != null ? String(patch.pd.left) : next.pd.left
      };
    }
    if (patch.notes != null) next.notes = String(patch.notes);
    if (patch.dob != null) next.dob = String(patch.dob);
    if (patch.gender != null) next.gender = String(patch.gender);
    if (isObject(patch.meta)) next.meta = assign({}, next.meta, patch.meta);
    if (isObject(patch.validation)) next.validation = createValidationState(assign({}, next.validation, patch.validation));
    if (patch.updatedAt !== undefined) next.updatedAt = patch.updatedAt;

    return next;
  }

  /* ---------------------------------------------------------------------------
   * Upload Prescription state — upload-specific only (no product / pricing / cart)
   * ------------------------------------------------------------------------- */
  function createUploadValidation(overrides) {
    var state = {
      valid: true,
      errors: {},
      message: ''
    };
    if (!isObject(overrides)) return state;
    if (overrides.valid === false) state.valid = false;
    if (isObject(overrides.errors)) state.errors = shallowClone(overrides.errors);
    if (overrides.message != null) state.message = String(overrides.message);
    return state;
  }

  function normalizeUploadStatus(value) {
    var key = String(value || '')
      .toLowerCase()
      .trim();
    if (
      key === UPLOAD_STATUS.IDLE ||
      key === UPLOAD_STATUS.SELECTING ||
      key === UPLOAD_STATUS.VALIDATING ||
      key === UPLOAD_STATUS.READY ||
      key === UPLOAD_STATUS.SUBMITTING ||
      key === UPLOAD_STATUS.COMPLETED ||
      key === UPLOAD_STATUS.ERROR
    ) {
      return key;
    }
    return UPLOAD_STATUS.IDLE;
  }

  /**
   * Dedicated uploadPrescription branch shape for lensFlow.
   * selectedFile holds the in-memory File/Blob until Confirm / provider handoff.
   */
  function createUploadPrescription(overrides) {
    overrides = isObject(overrides) ? overrides : {};

    return {
      patientName: overrides.patientName != null ? String(overrides.patientName) : '',
      selectedFile: overrides.selectedFile != null ? overrides.selectedFile : null,
      fileName: overrides.fileName != null ? String(overrides.fileName) : '',
      fileSize: overrides.fileSize != null ? Number(overrides.fileSize) || 0 : 0,
      mimeType: overrides.mimeType != null ? String(overrides.mimeType) : '',
      uploadProgress: overrides.uploadProgress != null ? Number(overrides.uploadProgress) || 0 : 0,
      uploadStatus: normalizeUploadStatus(overrides.uploadStatus),
      additionalInformation:
        overrides.additionalInformation != null ? String(overrides.additionalInformation) : '',
      dob: overrides.dob != null ? String(overrides.dob) : '',
      gender: overrides.gender != null ? String(overrides.gender) : '',
      confirmed: overrides.confirmed === true,
      validation: createUploadValidation(overrides.validation),
      updatedAt: overrides.updatedAt != null ? overrides.updatedAt : null
    };
  }

  function cloneUploadPrescription(state) {
    var next = createUploadPrescription(state || {});
    /* Preserve the same File/Blob reference — do not serialize. */
    next.selectedFile = state && state.selectedFile != null ? state.selectedFile : null;
    return next;
  }

  function mergeUploadPrescription(base, patch) {
    var next = cloneUploadPrescription(base);
    if (!isObject(patch)) return next;

    if (patch.patientName != null) next.patientName = String(patch.patientName);
    if (patch.selectedFile !== undefined) next.selectedFile = patch.selectedFile;
    if (patch.fileName != null) next.fileName = String(patch.fileName);
    if (patch.fileSize != null) next.fileSize = Number(patch.fileSize) || 0;
    if (patch.mimeType != null) next.mimeType = String(patch.mimeType);
    if (patch.uploadProgress != null) next.uploadProgress = Number(patch.uploadProgress) || 0;
    if (patch.uploadStatus != null) next.uploadStatus = normalizeUploadStatus(patch.uploadStatus);
    if (patch.additionalInformation != null) {
      next.additionalInformation = String(patch.additionalInformation);
    }
    if (patch.dob != null) next.dob = String(patch.dob);
    if (patch.gender != null) next.gender = String(patch.gender);
    if (patch.confirmed !== undefined) next.confirmed = patch.confirmed === true;
    if (isObject(patch.validation)) {
      next.validation = createUploadValidation(assign({}, next.validation, patch.validation));
    }
    if (patch.updatedAt !== undefined) next.updatedAt = patch.updatedAt;

    return next;
  }

  function fileExtension(fileName) {
    var name = String(fileName || '');
    var dot = name.lastIndexOf('.');
    if (dot < 0 || dot === name.length - 1) return '';
    return name.slice(dot + 1).toLowerCase();
  }

  function toFileList(input) {
    if (!input) return [];
    if (typeof FileList !== 'undefined' && input instanceof FileList) {
      return Array.prototype.slice.call(input);
    }
    if (Array.isArray(input)) return input.slice();
    return [input];
  }

  /* ---------------------------------------------------------------------------
   * File Validation Service — upload rules only (no UI)
   * ------------------------------------------------------------------------- */
  function FileValidationService(options) {
    options = options || {};
    this.maxFiles = options.maxFiles != null ? Number(options.maxFiles) : UPLOAD_FILE_LIMITS.MAX_FILES;
    this.maxSizeBytes =
      options.maxSizeBytes != null ? Number(options.maxSizeBytes) : UPLOAD_FILE_LIMITS.MAX_SIZE_BYTES;
    this.allowedMimeTypes = isObject(options.allowedMimeTypes)
      ? options.allowedMimeTypes
      : UPLOAD_ALLOWED_MIME;
    this.allowedExtensions = isObject(options.allowedExtensions)
      ? options.allowedExtensions
      : UPLOAD_ALLOWED_EXTENSIONS;
  }

  FileValidationService.prototype.createResult = function (overrides) {
    overrides = overrides || {};
    return {
      valid: overrides.valid !== false,
      errors: isObject(overrides.errors) ? shallowClone(overrides.errors) : {},
      message: overrides.message || '',
      file: overrides.file != null ? overrides.file : null,
      fileName: overrides.fileName != null ? String(overrides.fileName) : '',
      fileSize: overrides.fileSize != null ? Number(overrides.fileSize) || 0 : 0,
      mimeType: overrides.mimeType != null ? String(overrides.mimeType) : '',
      maxFiles: this.maxFiles,
      maxSizeBytes: this.maxSizeBytes
    };
  };

  FileValidationService.prototype.isAllowedType = function (file) {
    if (!file) return false;
    var mime = String(file.type || '')
      .toLowerCase()
      .trim();
    var ext = fileExtension(file.name);

    if (mime && this.allowedMimeTypes[mime]) return true;
    if (ext && this.allowedExtensions[ext]) return true;
    return false;
  };

  /**
   * Validate a File / FileList / array.
   * Returns a normalized result — never touches the DOM.
   */
  FileValidationService.prototype.validate = function (input) {
    var files = toFileList(input);
    var errors = {};
    var message = '';

    if (!files.length) {
      errors.file = 'required';
      message = 'A prescription file is required.';
      return this.createResult({
        valid: false,
        errors: errors,
        message: message
      });
    }

    if (files.length > this.maxFiles) {
      errors.file = 'max_files';
      message = 'Only one prescription file is allowed.';
      return this.createResult({
        valid: false,
        errors: errors,
        message: message,
        file: files[0] || null,
        fileName: files[0] && files[0].name ? String(files[0].name) : '',
        fileSize: files[0] && files[0].size != null ? Number(files[0].size) || 0 : 0,
        mimeType: files[0] && files[0].type ? String(files[0].type) : ''
      });
    }

    var file = files[0];
    var fileName = file && file.name != null ? String(file.name) : '';
    var fileSize = file && file.size != null ? Number(file.size) || 0 : 0;
    var mimeType = file && file.type != null ? String(file.type) : '';

    if (fileSize > this.maxSizeBytes) {
      errors.fileSize = 'max_size';
      message = 'File must be 5 MB or smaller.';
      return this.createResult({
        valid: false,
        errors: errors,
        message: message,
        file: file,
        fileName: fileName,
        fileSize: fileSize,
        mimeType: mimeType
      });
    }

    if (!this.isAllowedType(file)) {
      errors.mimeType = 'invalid_type';
      message = 'Allowed types: PDF, JPG, JPEG, PNG, HEIC.';
      return this.createResult({
        valid: false,
        errors: errors,
        message: message,
        file: file,
        fileName: fileName,
        fileSize: fileSize,
        mimeType: mimeType
      });
    }

    return this.createResult({
      valid: true,
      errors: {},
      message: '',
      file: file,
      fileName: fileName,
      fileSize: fileSize,
      mimeType: mimeType || this.inferMimeFromExtension(fileName)
    });
  };

  FileValidationService.prototype.inferMimeFromExtension = function (fileName) {
    var ext = fileExtension(fileName);
    if (ext === 'pdf') return 'application/pdf';
    if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
    if (ext === 'png') return 'image/png';
    if (ext === 'heic') return 'image/heic';
    if (ext === 'heif') return 'image/heif';
    return '';
  };

  /* ---------------------------------------------------------------------------
   * Upload Service — business state only (no DOM / render / events binding)
   * ------------------------------------------------------------------------- */
  function UploadService(options) {
    options = options || {};
    this.provider = options.provider || null;
    this.fileValidator = options.fileValidator || new FileValidationService();
    this.stateRef = options.stateRef || null;
    this.stateKey = options.stateKey || 'uploadPrescription';
    this.emit = options.emit || null;
    this._ready = false;
  }

  UploadService.prototype._readState = function () {
    if (!this.stateRef || !this.stateKey) return null;
    return this.stateRef[this.stateKey] || null;
  };

  UploadService.prototype._writeState = function (state) {
    if (!this.stateRef || !this.stateKey) return state;
    this.stateRef[this.stateKey] = state;
    return state;
  };

  UploadService.prototype._commit = function (patch, eventName, detail) {
    var current = this._readState() || createUploadPrescription();
    var next = mergeUploadPrescription(current, patch);
    next.updatedAt = Date.now();
    this._writeState(next);

    emitSafe(this.emit, eventName || EVENTS.UPLOAD_UPDATED, assign({
      source: SOURCES.UPLOAD,
      upload: cloneUploadPrescription(next)
    }, detail || {}));

    return cloneUploadPrescription(next);
  };

  UploadService.prototype.initialize = function (seed) {
    var current = this._readState();
    var next = current
      ? mergeUploadPrescription(createUploadPrescription(seed), current)
      : createUploadPrescription(seed);

    this._writeState(next);
    this._ready = true;

    emitSafe(this.emit, EVENTS.UPLOAD_INITIALIZED, {
      source: SOURCES.UPLOAD,
      upload: cloneUploadPrescription(next),
      provider: this.provider && this.provider.name ? this.provider.name : null
    });

    return cloneUploadPrescription(next);
  };

  UploadService.prototype.exportState = function () {
    var current = this._readState();
    if (current) return cloneUploadPrescription(current);
    return createUploadPrescription();
  };

  UploadService.prototype.selectFile = function (fileInput) {
    this._commit(
      {
        uploadStatus: UPLOAD_STATUS.SELECTING,
        confirmed: false,
        uploadProgress: 0
      },
      EVENTS.UPLOAD_UPDATED,
      { action: 'selecting' }
    );

    this._commit(
      {
        uploadStatus: UPLOAD_STATUS.VALIDATING
      },
      EVENTS.UPLOAD_UPDATED,
      { action: 'validating' }
    );

    var result = this.fileValidator.validate(fileInput);

    if (!result.valid) {
      return this._commit(
        {
          selectedFile: null,
          fileName: result.fileName || '',
          fileSize: result.fileSize || 0,
          mimeType: result.mimeType || '',
          uploadProgress: 0,
          uploadStatus: UPLOAD_STATUS.ERROR,
          confirmed: false,
          validation: {
            valid: false,
            errors: result.errors,
            message: result.message
          }
        },
        EVENTS.UPLOAD_VALIDATION_FAILED,
        { result: result, action: 'selectFile' }
      );
    }

    var ready = this._commit(
      {
        selectedFile: result.file,
        fileName: result.fileName,
        fileSize: result.fileSize,
        mimeType: result.mimeType,
        uploadProgress: 100,
        uploadStatus: UPLOAD_STATUS.READY,
        confirmed: false,
        validation: {
          valid: true,
          errors: {},
          message: ''
        }
      },
      EVENTS.UPLOAD_FILE_SELECTED,
      { result: result, action: 'selectFile' }
    );

    emitSafe(this.emit, EVENTS.UPLOAD_READY, {
      source: SOURCES.UPLOAD,
      upload: ready,
      action: 'selectFile'
    });

    return ready;
  };

  UploadService.prototype.replaceFile = function (fileInput) {
    return this.selectFile(fileInput);
  };

  UploadService.prototype.removeFile = function () {
    return this._commit(
      {
        selectedFile: null,
        fileName: '',
        fileSize: 0,
        mimeType: '',
        uploadProgress: 0,
        uploadStatus: UPLOAD_STATUS.IDLE,
        confirmed: false,
        validation: {
          valid: true,
          errors: {},
          message: ''
        }
      },
      EVENTS.UPLOAD_FILE_REMOVED,
      { action: 'removeFile' }
    );
  };

  UploadService.prototype.setPatientName = function (value) {
    return this._commit(
      { patientName: value != null ? String(value) : '' },
      EVENTS.UPLOAD_UPDATED,
      { action: 'setPatientName' }
    );
  };

  UploadService.prototype.setAdditionalInformation = function (value) {
    return this._commit(
      { additionalInformation: value != null ? String(value) : '' },
      EVENTS.UPLOAD_UPDATED,
      { action: 'setAdditionalInformation' }
    );
  };

  UploadService.prototype.setDOB = function (value) {
    return this._commit(
      { dob: value != null ? String(value) : '' },
      EVENTS.UPLOAD_UPDATED,
      { action: 'setDOB' }
    );
  };

  UploadService.prototype.setGender = function (value) {
    return this._commit(
      { gender: value != null ? String(value) : '' },
      EVENTS.UPLOAD_UPDATED,
      { action: 'setGender' }
    );
  };

  /**
   * Confirm path — maps upload state into the normalized prescription model
   * and mirrors via Provider. File remains in browser memory (no network upload).
   */
  UploadService.prototype.prepareSubmission = function () {
    var current = this._readState() || createUploadPrescription();

    if (current.uploadStatus !== UPLOAD_STATUS.READY && current.uploadStatus !== UPLOAD_STATUS.COMPLETED) {
      var gate = this.fileValidator.validate(current.selectedFile);
      if (!gate.valid || !current.selectedFile) {
        return this._commit(
          {
            uploadStatus: UPLOAD_STATUS.ERROR,
            confirmed: false,
            validation: {
              valid: false,
              errors: assign({ file: 'required' }, gate.errors || {}),
              message: gate.message || 'Select a valid prescription file before confirming.'
            }
          },
          EVENTS.UPLOAD_VALIDATION_FAILED,
          { action: 'prepareSubmission', result: gate }
        );
      }
    }

    this._commit(
      {
        uploadStatus: UPLOAD_STATUS.SUBMITTING,
        confirmed: true
      },
      EVENTS.UPLOAD_UPDATED,
      { action: 'submitting' }
    );

    current = this._readState() || current;

    var normalized = createNormalizedPrescription({
      source: SOURCES.UPLOAD,
      patientName: current.patientName,
      notes: current.additionalInformation,
      dob: current.dob,
      gender: current.gender,
      meta: {
        strategy: SOURCES.UPLOAD,
        fileName: current.fileName,
        fileSize: current.fileSize,
        mimeType: current.mimeType,
        uploadStatus: UPLOAD_STATUS.SUBMITTING,
        hasInMemoryFile: !!current.selectedFile
      },
      updatedAt: Date.now()
    });

    var providerResult = null;
    if (this.provider && typeof this.provider.save === 'function') {
      providerResult = this.provider.save(normalized);
    }

    var completed = this._commit(
      {
        uploadStatus: UPLOAD_STATUS.COMPLETED,
        confirmed: true,
        uploadProgress: 100,
        validation: {
          valid: true,
          errors: {},
          message: ''
        }
      },
      EVENTS.UPLOAD_SUBMISSION_PREPARED,
      {
        action: 'prepareSubmission',
        prescription: normalized,
        providerResult: providerResult
      }
    );

    return {
      ok: true,
      upload: completed,
      prescription: normalized,
      providerResult: providerResult,
      /* File handle stays on upload state — not sent yet. */
      selectedFile: completed.selectedFile
    };
  };

  UploadService.prototype.reset = function () {
    var next = createUploadPrescription();
    this._writeState(next);
    this._ready = true;

    emitSafe(this.emit, EVENTS.UPLOAD_RESET, {
      source: SOURCES.UPLOAD,
      upload: cloneUploadPrescription(next),
      provider: this.provider && this.provider.name ? this.provider.name : null
    });

    return cloneUploadPrescription(next);
  };

  /**
   * Map upload state → normalized prescription (for future Review / cart handoff).
   */
  UploadService.prototype.toNormalizedPrescription = function () {
    var current = this._readState() || createUploadPrescription();
    return createNormalizedPrescription({
      source: SOURCES.UPLOAD,
      patientName: current.patientName,
      notes: current.additionalInformation,
      dob: current.dob,
      gender: current.gender,
      meta: {
        strategy: SOURCES.UPLOAD,
        fileName: current.fileName,
        fileSize: current.fileSize,
        mimeType: current.mimeType,
        uploadStatus: current.uploadStatus,
        confirmed: current.confirmed,
        hasInMemoryFile: !!current.selectedFile
      },
      updatedAt: current.updatedAt
    });
  };

  /* ---------------------------------------------------------------------------
   * Provider abstraction — drawer never knows which provider is active
   * ------------------------------------------------------------------------- */
  function registerProvider(name, factory) {
    var key = String(name || '')
      .toLowerCase()
      .trim();
    if (!key || typeof factory !== 'function') return false;
    providerRegistry[key] = factory;
    return true;
  }

  function createProvider(name, options) {
    var key = String(name || DEFAULT_PROVIDER)
      .toLowerCase()
      .trim();
    var factory = providerRegistry[key] || providerRegistry[DEFAULT_PROVIDER];
    if (typeof factory !== 'function') {
      throw new Error('BasariPrescription: no provider registered for "' + key + '"');
    }
    return factory(options || {});
  }

  /**
   * Mock Provider — stand-in until VisionCarePro (or other) integration is defined.
   * No network / SDK / API assumptions.
   */
  function MockProvider(options) {
    options = options || {};
    this.name = 'mock';
    this._storage = null;
    this._initialized = false;
    this._sourceDefault = options.source || SOURCES.MANUAL;
  }

  MockProvider.prototype.initialize = function (seed) {
    this._storage = createNormalizedPrescription(
      assign({ source: this._sourceDefault }, isObject(seed) ? seed : {})
    );
    this._initialized = true;
    return clonePrescription(this._storage);
  };

  MockProvider.prototype.save = function (prescription) {
    var normalized = createNormalizedPrescription(
      assign({}, prescription || {}, {
        source: (prescription && prescription.source) || this._sourceDefault,
        updatedAt: Date.now()
      })
    );
    this._storage = normalized;
    this._initialized = true;
    return {
      ok: true,
      provider: this.name,
      prescription: clonePrescription(normalized)
    };
  };

  MockProvider.prototype.load = function (id) {
    if (!this._initialized || !this._storage) {
      return this.initialize();
    }
    if (id != null && this._storage.id != null && String(id) !== String(this._storage.id)) {
      return null;
    }
    return clonePrescription(this._storage);
  };

  MockProvider.prototype.reset = function () {
    this._storage = createNormalizedPrescription({ source: this._sourceDefault });
    this._initialized = true;
    return clonePrescription(this._storage);
  };

  MockProvider.prototype.getNormalized = function () {
    if (!this._initialized || !this._storage) {
      return this.initialize();
    }
    return clonePrescription(this._storage);
  };

  registerProvider('mock', function (options) {
    return new MockProvider(options);
  });

  /* ---------------------------------------------------------------------------
   * Validation Service — architecture only (rules land in Phase B)
   * ------------------------------------------------------------------------- */
  function ValidationService() {}

  ValidationService.prototype.createResult = function (overrides) {
    overrides = overrides || {};
    return {
      valid: overrides.valid !== false,
      scope: overrides.scope || 'complete',
      errors: isObject(overrides.errors) ? shallowClone(overrides.errors) : {},
      sections: isObject(overrides.sections) ? shallowClone(overrides.sections) : {},
      message: overrides.message || ''
    };
  };

  /** Field-level hook — Phase B fills real rules. */
  ValidationService.prototype.validateField = function (field, value, context) {
    return this.createResult({
      valid: true,
      scope: 'field',
      errors: {},
      message: '',
      field: field,
      value: value,
      context: context || null
    });
  };

  /** Section-level hook (eyes / pd / patient / meta). */
  ValidationService.prototype.validateSection = function (section, prescription) {
    return this.createResult({
      valid: true,
      scope: 'section',
      sections: section
        ? (function () {
            var map = {};
            map[section] = { valid: true, errors: {} };
            return map;
          })()
        : {},
      message: ''
    });
  };

  /** Full-form hook — Phase B enforces completeness. */
  ValidationService.prototype.validateComplete = function (prescription) {
    return this.createResult({
      valid: true,
      scope: 'complete',
      errors: {},
      sections: {},
      message: ''
    });
  };

  /**
   * Provider validation hook.
   * Mock always passes; future providers may return async results.
   */
  ValidationService.prototype.validateWithProvider = function (prescription, provider) {
    if (provider && typeof provider.validate === 'function') {
      try {
        var providerResult = provider.validate(prescription);
        if (isObject(providerResult)) {
          return this.createResult(
            assign({ scope: 'provider', valid: providerResult.valid !== false }, providerResult)
          );
        }
      } catch (error) {
        return this.createResult({
          valid: false,
          scope: 'provider',
          message: error && error.message ? error.message : 'Provider validation failed'
        });
      }
    }

    return this.createResult({
      valid: true,
      scope: 'provider',
      message: ''
    });
  };

  ValidationService.prototype.validate = function (prescription, options) {
    options = options || {};
    var scope = options.scope || 'complete';
    var provider = options.provider || null;

    if (scope === 'field') {
      return this.validateField(options.field, options.value, options.context);
    }
    if (scope === 'section') {
      return this.validateSection(options.section, prescription);
    }
    if (scope === 'provider') {
      return this.validateWithProvider(prescription, provider);
    }

    var complete = this.validateComplete(prescription);
    if (!complete.valid) return complete;

    if (provider) {
      var providerCheck = this.validateWithProvider(prescription, provider);
      if (!providerCheck.valid) return providerCheck;
    }

    return complete;
  };

  /* ---------------------------------------------------------------------------
   * Prescription Service — public API for prescription operations
   * ------------------------------------------------------------------------- */
  function PrescriptionService(options) {
    options = options || {};
    this.provider = options.provider;
    this.validator = options.validator || new ValidationService();
    this.stateRef = options.stateRef || null;
    this.stateKey = options.stateKey || 'manualPrescription';
    this.emit = options.emit || null;
    this._ready = false;
  }

  PrescriptionService.prototype._readState = function () {
    if (!this.stateRef || !this.stateKey) return null;
    return this.stateRef[this.stateKey] || null;
  };

  PrescriptionService.prototype._writeState = function (prescription) {
    if (!this.stateRef || !this.stateKey) return prescription;
    this.stateRef[this.stateKey] = prescription;
    return prescription;
  };

  PrescriptionService.prototype.initialize = function (seed) {
    var fromProvider = this.provider.initialize(seed);
    var current = this._readState();
    var next = current
      ? mergePrescription(fromProvider, current)
      : createNormalizedPrescription(fromProvider);

    this._writeState(next);
    this._ready = true;

    emitSafe(this.emit, EVENTS.INITIALIZED, {
      source: next.source,
      prescription: clonePrescription(next),
      provider: this.provider.name
    });

    emitSafe(this.emit, EVENTS.PROVIDER_READY, {
      provider: this.provider.name
    });

    return clonePrescription(next);
  };

  PrescriptionService.prototype.update = function (patch) {
    var current = this._readState() || this.provider.getNormalized();
    var next = mergePrescription(current, patch);
    next.updatedAt = Date.now();
    this._writeState(next);

    if (this.provider && typeof this.provider.save === 'function') {
      // Keep provider mirror in sync without implying a remote save.
      this.provider.save(next);
    }

    emitSafe(this.emit, EVENTS.UPDATED, {
      source: next.source,
      patch: patch || {},
      prescription: clonePrescription(next)
    });

    return clonePrescription(next);
  };

  PrescriptionService.prototype.reset = function () {
    var next = this.provider.reset();
    this._writeState(next);

    emitSafe(this.emit, EVENTS.RESET, {
      source: next.source,
      prescription: clonePrescription(next),
      provider: this.provider.name
    });

    return clonePrescription(next);
  };

  PrescriptionService.prototype.validate = function (options) {
    options = options || {};
    var prescription = this._readState() || this.exportNormalized();

    emitSafe(this.emit, EVENTS.VALIDATION_REQUESTED, {
      scope: options.scope || 'complete',
      prescription: clonePrescription(prescription)
    });

    var result = this.validator.validate(prescription, {
      scope: options.scope,
      field: options.field,
      value: options.value,
      section: options.section,
      context: options.context,
      provider: this.provider
    });

    var validationState = createValidationState({
      status: result.valid ? VALIDATION_STATUS.VALID : VALIDATION_STATUS.INVALID,
      errors: result.errors,
      sections: result.sections
    });

    this._writeState(
      mergePrescription(prescription, {
        validation: validationState
      })
    );

    emitSafe(this.emit, result.valid ? EVENTS.VALIDATION_PASSED : EVENTS.VALIDATION_FAILED, {
      scope: result.scope,
      result: result,
      prescription: this.exportNormalized()
    });

    return result;
  };

  PrescriptionService.prototype.exportNormalized = function () {
    var current = this._readState();
    if (current) return clonePrescription(current);
    return this.provider.getNormalized();
  };

  PrescriptionService.prototype.save = function () {
    var prescription = this.exportNormalized();
    var result = this.provider.save(prescription);
    var saved = result && result.prescription ? result.prescription : prescription;
    this._writeState(createNormalizedPrescription(saved));

    emitSafe(this.emit, EVENTS.SAVED, {
      provider: this.provider.name,
      prescription: clonePrescription(saved),
      result: result
    });

    return result;
  };

  PrescriptionService.prototype.load = function (id) {
    var loaded = this.provider.load(id);
    if (!loaded) return null;
    this._writeState(createNormalizedPrescription(loaded));
    return this.exportNormalized();
  };

  /* ---------------------------------------------------------------------------
   * Prescription Controller — orchestration only (no rules / no provider logic)
   * Manual + Upload strategies share this single controller.
   * ------------------------------------------------------------------------- */
  function PrescriptionController(options) {
    options = options || {};
    this.service = options.service;
    this.uploadService = options.uploadService || null;
    this.navigate = typeof options.navigate === 'function' ? options.navigate : null;
    this.emit = options.emit || null;
    this.initialized = false;
    this.uploadInitialized = false;
  }

  PrescriptionController.prototype.initialize = function (seed) {
    if (this.initialized) {
      return this.service.exportNormalized();
    }
    var prescription = this.service.initialize(seed);
    this.initialized = true;
    return prescription;
  };

  PrescriptionController.prototype.load = function (id) {
    this.initialize();
    return this.service.load(id);
  };

  PrescriptionController.prototype.update = function (patch) {
    this.initialize();
    return this.service.update(patch);
  };

  PrescriptionController.prototype.validate = function (options) {
    this.initialize();
    return this.service.validate(options);
  };

  PrescriptionController.prototype.save = function () {
    this.initialize();
    return this.service.save();
  };

  PrescriptionController.prototype.reset = function () {
    if (!this.initialized) {
      return this.initialize();
    }
    return this.service.reset();
  };

  PrescriptionController.prototype.getPrescription = function () {
    this.initialize();
    return this.service.exportNormalized();
  };

  /* --- Upload strategy delegation (UploadService / FileValidationService) --- */

  PrescriptionController.prototype._ensureUploadService = function () {
    if (!this.uploadService) {
      throw new Error('BasariPrescription: UploadService is not configured on this controller');
    }
    return this.uploadService;
  };

  PrescriptionController.prototype.initializeUpload = function (seed) {
    var uploadService = this._ensureUploadService();
    if (this.uploadInitialized) {
      return uploadService.exportState();
    }
    var state = uploadService.initialize(seed);
    this.uploadInitialized = true;
    return state;
  };

  PrescriptionController.prototype.selectUploadFile = function (fileInput) {
    this.initializeUpload();
    return this._ensureUploadService().selectFile(fileInput);
  };

  PrescriptionController.prototype.replaceUploadFile = function (fileInput) {
    this.initializeUpload();
    return this._ensureUploadService().replaceFile(fileInput);
  };

  PrescriptionController.prototype.removeUploadFile = function () {
    this.initializeUpload();
    return this._ensureUploadService().removeFile();
  };

  PrescriptionController.prototype.setUploadPatientName = function (value) {
    this.initializeUpload();
    return this._ensureUploadService().setPatientName(value);
  };

  PrescriptionController.prototype.setUploadAdditionalInformation = function (value) {
    this.initializeUpload();
    return this._ensureUploadService().setAdditionalInformation(value);
  };

  PrescriptionController.prototype.setUploadDOB = function (value) {
    this.initializeUpload();
    return this._ensureUploadService().setDOB(value);
  };

  PrescriptionController.prototype.setUploadGender = function (value) {
    this.initializeUpload();
    return this._ensureUploadService().setGender(value);
  };

  PrescriptionController.prototype.prepareUploadSubmission = function () {
    this.initializeUpload();
    return this._ensureUploadService().prepareSubmission();
  };

  PrescriptionController.prototype.resetUpload = function () {
    var uploadService = this._ensureUploadService();
    if (!this.uploadInitialized) {
      return this.initializeUpload();
    }
    return uploadService.reset();
  };

  PrescriptionController.prototype.getUploadPrescription = function () {
    this.initializeUpload();
    return this._ensureUploadService().exportState();
  };

  PrescriptionController.prototype.validateUploadFile = function (fileInput) {
    this.initializeUpload();
    return this._ensureUploadService().fileValidator.validate(fileInput);
  };

  PrescriptionController.prototype.exportUploadNormalized = function () {
    this.initializeUpload();
    return this._ensureUploadService().toNormalizedPrescription();
  };

  /**
   * Request navigation through the host drawer router.
   * Controller never owns routing — it only asks.
   */
  PrescriptionController.prototype.requestNavigation = function (destination, options) {
    var next = String(destination || '').trim();
    options = options || {};

    emitSafe(this.emit, EVENTS.NAVIGATION_REQUESTED, {
      destination: next || null,
      options: options
    });

    if (next && this.navigate) {
      this.navigate(next, options);
    }

    return !!next;
  };

  /**
   * Temporary Confirm bridge (1B.14 Phase A).
   * Manual / Upload Confirm → Lens Add-ons drawer.
   * No validation, upload transfer, VisionCarePro, or cart — navigation only.
   */
  PrescriptionController.prototype.proceedToLensAddons = function (options) {
    return this.requestNavigation('lens-addons', options || {});
  };

  /**
   * Wire a full stack for a drawer instance.
   * Provider defaults to Mock; swap via providerName when a real provider exists.
   * Manual (PrescriptionService) + Upload (UploadService) share one controller + provider.
   */
  function createFramework(options) {
    options = options || {};

    var provider = createProvider(options.providerName || DEFAULT_PROVIDER, options.providerOptions || {});
    var validator = options.validator || new ValidationService();
    var fileValidator = options.fileValidator || new FileValidationService(options.fileValidationOptions || {});
    var service = new PrescriptionService({
      provider: provider,
      validator: validator,
      stateRef: options.stateRef,
      stateKey: options.stateKey || 'manualPrescription',
      emit: options.emit
    });
    var uploadService = new UploadService({
      provider: provider,
      fileValidator: fileValidator,
      stateRef: options.stateRef,
      stateKey: options.uploadStateKey || 'uploadPrescription',
      emit: options.emit
    });
    var controller = new PrescriptionController({
      service: service,
      uploadService: uploadService,
      navigate: options.navigate,
      emit: options.emit
    });

    return {
      provider: provider,
      validator: validator,
      fileValidator: fileValidator,
      service: service,
      uploadService: uploadService,
      controller: controller
    };
  }

  BasariPrescription.EVENTS = EVENTS;
  BasariPrescription.SOURCES = SOURCES;
  BasariPrescription.PD_MODES = PD_MODES;
  BasariPrescription.VALIDATION_STATUS = VALIDATION_STATUS;
  BasariPrescription.UPLOAD_STATUS = UPLOAD_STATUS;
  BasariPrescription.UPLOAD_FILE_LIMITS = UPLOAD_FILE_LIMITS;
  BasariPrescription.createEye = createEye;
  BasariPrescription.createValidationState = createValidationState;
  BasariPrescription.createNormalizedPrescription = createNormalizedPrescription;
  BasariPrescription.clonePrescription = clonePrescription;
  BasariPrescription.mergePrescription = mergePrescription;
  BasariPrescription.createUploadPrescription = createUploadPrescription;
  BasariPrescription.cloneUploadPrescription = cloneUploadPrescription;
  BasariPrescription.mergeUploadPrescription = mergeUploadPrescription;
  BasariPrescription.registerProvider = registerProvider;
  BasariPrescription.createProvider = createProvider;
  BasariPrescription.MockProvider = MockProvider;
  BasariPrescription.ValidationService = ValidationService;
  BasariPrescription.FileValidationService = FileValidationService;
  BasariPrescription.PrescriptionService = PrescriptionService;
  BasariPrescription.UploadService = UploadService;
  BasariPrescription.PrescriptionController = PrescriptionController;
  BasariPrescription.createFramework = createFramework;

  global.BasariPrescription = BasariPrescription;
})(typeof window !== 'undefined' ? window : this);
